import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { usePatients, useSubstances } from "@/lib/usePatients";
import { displayMedicationName, ensureCanonicalSubstanceFromInput, resolveSmartSubstance } from "@/lib/pharmacology/smartSubstance";
import { groupSubstances } from "@/lib/pharmacology/substanceCatalog";
import { savePatientMedication } from "@/lib/clinical/medicationPersistence";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/clinical/PageHeader";
import { EmptyState } from "@/components/clinical/EmptyState";
import { RegimenIntelligencePanel } from "@/components/clinical/RegimenIntelligencePanel";
import { MedicationLongitudinalPanel } from "@/components/clinical/MedicationLongitudinalPanel";
import { BookOpen, ChevronLeft, ChevronRight, History as HistoryIcon, Pencil, Pill, Plus, Search, ShieldOff, Wand2 } from "lucide-react";
import { toast } from "@/lib/safeToast";

const EMPTY_FORM = {
  patient_id: "",
  medication_query: "",
  brand_name: "",
  free_text_name: "",
  substance_id: "",
  formulation_id: "",
  current_dose: "",
  dose_unit: "mg",
  frequency: "1x/dia",
  status: "ativo",
  usual_time: "",
  start_date: "",
  expected_benefit_0_100: "",
  expected_cost_0_100: "",
  individual_sensitivity_0_100: "",
  individual_tolerance_0_100: "",
  redose_risk_individual_0_100: "",
  psychosis_activation_risk_individual_0_100: "",
  clinical_rationale: "",
  monitoring_plan: "",
  warning_signs: "",
  sedation_sensitivity_0_100: "",
  insomnia_sensitivity_0_100: "",
  autonomic_sensitivity_0_100: "",
  abuse_risk_individual_0_100: "",
  withdrawal_risk_individual_0_100: "",
  individual_carry_over: "",
  indication: "",
  diagnostic_target: "",
  target_symptoms: "",
  expected_improvements: "",
  potential_worsening: "",
  stop_or_reduce_criteria: "",
  discontinuation_reason: "",
  end_date: "",
  physician_notes: "",
  patient_notes: "",
};

const FORM_SECTIONS = [
  ["identity", "Identificação e vínculo"],
  ["regimen", "Esquema e status"],
  ["targets", "Alvos e desfechos"],
  ["response", "Resposta individual"],
  ["monitoring", "Manejo e monitorização"],
] as const;

function numberOrNull(value: any) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function listOrNull(value: any): string[] | null {
  const items = String(value ?? "")
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length ? items : null;
}

function listToText(value: any) {
  return Array.isArray(value) ? value.join(", ") : String(value ?? "");
}

export default function Medications() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: patients = [] } = usePatients();
  const { data: substances = [] } = useSubstances();
  const groups = useMemo(() => groupSubstances(substances as any[], { userId: user?.id }), [substances, user?.id]);

  const { data: medications = [], isLoading, error } = useQuery({
    queryKey: ["all-meds"],
    queryFn: async () => {
      const { data, error: readError } = await (supabase as any)
        .from("patient_medications")
        .select("*, patients(full_name), substances(*), substance_formulations(*)")
        .order("created_at", { ascending: false });
      if (readError) throw readError;
      return data ?? [];
    },
  });

  const [patientFilter, setPatientFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<any>({ ...EMPTY_FORM });
  const [formSection, setFormSection] = useState("identity");
  const [aliasMessage, setAliasMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [longitudinalMedication, setLongitudinalMedication] = useState<any | null>(null);

  const resolution = useMemo(
    () => form.medication_query ? resolveSmartSubstance(form.medication_query, substances as any[]) : null,
    [form.medication_query, substances],
  );

  const { data: formulations = [] } = useQuery({
    queryKey: ["med-formulations", form.substance_id],
    enabled: !!form.substance_id,
    queryFn: async () => {
      const { data, error: readError } = await (supabase as any)
        .from("substance_formulations")
        .select("*")
        .eq("substance_id", form.substance_id)
        .order("is_default", { ascending: false })
        .order("formulation_name");
      if (readError) throw readError;
      return data ?? [];
    },
  });

  const visibleMedications = patientFilter
    ? medications.filter((medication: any) => medication.patient_id === patientFilter)
    : medications;

  const currentSectionIndex = Math.max(0, FORM_SECTIONS.findIndex(([id]) => id === formSection));
  const previousSection = FORM_SECTIONS[currentSectionIndex - 1]?.[0];
  const nextSection = FORM_SECTIONS[currentSectionIndex + 1]?.[0];

  function resetDialog(patientId = "") {
    setEditingId(null);
    setAliasMessage("");
    setFormSection("identity");
    setForm({ ...EMPTY_FORM, patient_id: patientId });
  }

  function openNew() {
    resetDialog(patientFilter);
    setOpen(true);
  }

  function openEdit(medication: any) {
    setEditingId(medication.id);
    setAliasMessage("");
    setFormSection("identity");
    const normalized = Object.fromEntries(
      Object.keys(EMPTY_FORM).map((key) => {
        const raw = (medication as any)[key];
        if (raw == null) return [key, (EMPTY_FORM as any)[key]];
        return [key, Array.isArray(raw) ? listToText(raw) : raw];
      }),
    );
    setForm({
      ...EMPTY_FORM,
      ...normalized,
      medication_query: medication.brand_name || medication.substances?.name || medication.free_text_name || "",
      substance_id: medication.substance_id || "",
      formulation_id: medication.formulation_id || "",
      current_dose: medication.current_dose ?? "",
      usual_time: medication.usual_time?.slice?.(0, 5) ?? medication.usual_time ?? "",
      start_date: medication.start_date ?? "",
      end_date: medication.end_date ?? "",
    });
    setOpen(true);
  }

  async function selectCanonical(substanceId: string) {
    const group = groups.find((item) => item.primary.id === substanceId || item.all.some((row) => row.id === substanceId));
    const substance = group?.primary ?? (substances as any[]).find((item) => item.id === substanceId);
    setForm((current: any) => ({
      ...current,
      substance_id: substance?.id ?? substanceId,
      formulation_id: "",
      dose_unit: substance?.default_dose_unit ?? substance?.dose_unit_default ?? current.dose_unit,
      free_text_name: current.brand_name && substance?.name ? `${current.brand_name} → ${substance.name}` : current.free_text_name,
    }));
    if (!substance?.id) return;
    const { data: forms = [] } = await (supabase as any)
      .from("substance_formulations")
      .select("id")
      .eq("substance_id", substance.id)
      .order("is_default", { ascending: false })
      .limit(1);
    if (forms?.[0]?.id) setForm((current: any) => ({ ...current, formulation_id: forms[0].id }));
  }

  async function resolveMedicationName() {
    if (!user) return toast.error("Sessão expirada.");
    if (!form.medication_query.trim()) return toast.error("Digite marca, genérico ou princípio ativo.");
    setAliasMessage(resolution?.message ?? "");
    if (!resolution?.template) {
      setForm((current: any) => ({ ...current, brand_name: current.brand_name || current.medication_query, free_text_name: current.free_text_name || current.medication_query }));
      return toast.warning("Sem correspondência segura. Você pode salvar manualmente ou criar o canônico no Laboratório PK/PD.");
    }

    try {
      const ensured = await ensureCanonicalSubstanceFromInput(form.medication_query, user, substances as any[]);
      const { data: forms = [] } = await (supabase as any)
        .from("substance_formulations")
        .select("*")
        .eq("substance_id", ensured.substance.id)
        .order("is_default", { ascending: false })
        .order("formulation_name");
      const defaultForm = forms?.[0];
      setForm((current: any) => ({
        ...current,
        brand_name: current.brand_name || current.medication_query,
        free_text_name: `${current.brand_name || current.medication_query} → ${ensured.substance.name}`,
        substance_id: ensured.substance.id,
        formulation_id: defaultForm?.id ?? "",
        dose_unit: ensured.substance.default_dose_unit ?? ensured.substance.dose_unit_default ?? current.dose_unit,
        clinical_rationale: current.clinical_rationale || ensured.template?.mechanism_summary || "",
        monitoring_plan: current.monitoring_plan || (ensured.template?.monitoring_profile ?? []).join(", "),
      }));
      await qc.invalidateQueries({ queryKey: ["substances"] });
      toast.success(ensured.created ? `${ensured.substance.name} criada e vinculada.` : `${ensured.substance.name} reutilizada sem duplicar.`);
    } catch (resolveError: any) {
      console.error("[Medications] resolve failed", resolveError);
      toast.error(resolveError?.message ?? "Falha ao resolver medicação.");
    }
  }

  async function save() {
    if (!user?.id) return toast.error("Sessão expirada.");
    if (!form.patient_id) {
      setFormSection("identity");
      return toast.error("Selecione o paciente.");
    }
    if (!String(form.brand_name || form.medication_query || form.free_text_name).trim()) {
      setFormSection("identity");
      return toast.error("Informe a medicação.");
    }
    setSaving(true);
    try {
      const result = await savePatientMedication({
        owner_id: user.id,
        patient_id: form.patient_id,
        substance_id: form.substance_id || null,
        formulation_id: form.formulation_id || null,
        brand_name: form.brand_name || form.medication_query || null,
        free_text_name: form.free_text_name || form.medication_query || null,
        current_dose: numberOrNull(form.current_dose),
        dose_unit: form.dose_unit || null,
        frequency: form.frequency || null,
        usual_time: form.usual_time || null,
        start_date: form.start_date || null,
        status: form.status || "ativo",
        expected_benefit_0_100: numberOrNull(form.expected_benefit_0_100),
        expected_cost_0_100: numberOrNull(form.expected_cost_0_100),
        individual_sensitivity_0_100: numberOrNull(form.individual_sensitivity_0_100),
        individual_tolerance_0_100: numberOrNull(form.individual_tolerance_0_100),
        redose_risk_individual_0_100: numberOrNull(form.redose_risk_individual_0_100),
        psychosis_activation_risk_individual_0_100: numberOrNull(form.psychosis_activation_risk_individual_0_100),
        clinical_rationale: form.clinical_rationale || null,
        monitoring_plan: form.monitoring_plan || null,
        warning_signs: form.warning_signs || null,
        sedation_sensitivity_0_100: numberOrNull(form.sedation_sensitivity_0_100),
        insomnia_sensitivity_0_100: numberOrNull(form.insomnia_sensitivity_0_100),
        autonomic_sensitivity_0_100: numberOrNull(form.autonomic_sensitivity_0_100),
        abuse_risk_individual_0_100: numberOrNull(form.abuse_risk_individual_0_100),
        withdrawal_risk_individual_0_100: numberOrNull(form.withdrawal_risk_individual_0_100),
        individual_carry_over: numberOrNull(form.individual_carry_over),
        indication: form.indication || null,
        diagnostic_target: form.diagnostic_target || null,
        target_symptoms: listOrNull(form.target_symptoms),
        expected_improvements: listOrNull(form.expected_improvements),
        potential_worsening: listOrNull(form.potential_worsening),
        stop_or_reduce_criteria: form.stop_or_reduce_criteria || null,
        discontinuation_reason: form.discontinuation_reason || null,
        end_date: form.end_date || null,
        physician_notes: form.physician_notes || null,
        patient_notes: form.patient_notes || null,
      }, editingId);
      await qc.invalidateQueries({ queryKey: ["all-meds"] });
      toast.success(result.consolidated ? "Tratamento existente consolidado; não foi criada duplicata." : editingId ? "Tratamento atualizado." : "Tratamento salvo e persistido.");
      setOpen(false);
      resetDialog(patientFilter);
    } catch (saveError: any) {
      toast.error(saveError?.message ?? "Falha ao salvar tratamento.");
    } finally {
      setSaving(false);
    }
  }

  async function suspend(medication: any) {
    if (!user?.id) return;
    const nextStatus = medication.status === "suspenso" ? "ativo" : "suspenso";
    const { error: updateError } = await (supabase as any)
      .from("patient_medications")
      .update({ status: nextStatus })
      .eq("id", medication.id)
      .eq("owner_id", user.id);
    if (updateError) return toast.error(`Falha ao alterar status: ${updateError.message}`);
    await qc.invalidateQueries({ queryKey: ["all-meds"] });
    toast.success(nextStatus === "suspenso" ? "Tratamento suspenso sem apagar histórico." : "Tratamento reativado.");
  }

  const dialog = (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) resetDialog(patientFilter); }}>
      <DialogTrigger asChild><Button onClick={openNew}><Plus className="mr-1.5 h-4 w-4" />Nova medicação</Button></DialogTrigger>
      <DialogContent className="inset-0 top-0 flex h-[100dvh] max-h-[100dvh] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[94vh] sm:max-w-4xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
        <DialogHeader className="shrink-0 border-b bg-background/95 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur sm:px-6 sm:pt-5">
          <DialogTitle className="pr-8">{editingId ? "Editar tratamento" : "Adicionar tratamento ao paciente"}</DialogTitle>
          <div className="text-xs text-muted-foreground">Todos os campos clínicos e individuais permanecem editáveis. Marca, canônico e formulação continuam separados.</div>
        </DialogHeader>

        <Tabs value={formSection} onValueChange={setFormSection} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b bg-background/95 px-3 py-2 sm:px-4">
            <Label htmlFor="treatment-editor-section" className="mb-1 block text-[10px] uppercase tracking-wide text-muted-foreground sm:hidden">Seção · {currentSectionIndex + 1}/{FORM_SECTIONS.length}</Label>
            <select id="treatment-editor-section" className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:hidden" value={formSection} onChange={(event) => setFormSection(event.target.value)}>
              {FORM_SECTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <div className="hidden overflow-x-auto sm:block">
              <TabsList className="inline-flex h-11 w-max min-w-full justify-start gap-1 bg-transparent">
                {FORM_SECTIONS.map(([id, label]) => <TabsTrigger key={id} value={id} className="shrink-0 text-xs">{label}</TabsTrigger>)}
              </TabsList>
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6">
            <TabsContent value="identity" className="mt-0 min-w-0 space-y-4">
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">O nome exibido pode ser “Lyberdia”; o vínculo técnico permanece Lisdexanfetamina + formulação pró-fármaco. Salvar novamente consolida o item em vez de duplicar.</div>
              <Field label="Paciente"><select className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={form.patient_id} onChange={(event) => setForm({ ...form, patient_id: event.target.value })}><option value="">Selecione…</option>{patients.map((patient: any) => <option key={patient.id} value={patient.id}>{patient.full_name}</option>)}</select></Field>
              <div className="rounded-md border p-3 space-y-2">
                <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]"><Field label="Marca, genérico ou princípio ativo"><Input value={form.medication_query} onChange={(event) => setForm({ ...form, medication_query: event.target.value })} placeholder="Lyberdia, Atensina, Rivotril, escitalopram…" /></Field><Button type="button" variant="outline" className="w-full md:mt-6 md:w-auto" onClick={resolveMedicationName}><Search className="mr-1 h-4 w-4" />Resolver e vincular</Button></div>
                {resolution?.message ? <div className="break-words text-xs text-muted-foreground">{resolution.message}</div> : null}
                {aliasMessage ? <div className="break-words text-xs text-primary">{aliasMessage}</div> : null}
              </div>
              <div className="grid gap-3 md:grid-cols-2"><Field label="Nome exibido no paciente"><Input value={form.brand_name} onChange={(event) => setForm({ ...form, brand_name: event.target.value })} /></Field><Field label="Descrição interna"><Input value={form.free_text_name} onChange={(event) => setForm({ ...form, free_text_name: event.target.value })} /></Field></div>
              <Field label="Substância canônica"><select className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={form.substance_id} onChange={(event) => selectCanonical(event.target.value)}><option value="">— sem vínculo técnico —</option>{groups.map((group) => <option key={group.key} value={group.primary.id}>{group.canonicalName}{group.duplicates.length ? ` · ${group.duplicates.length + 1} registros agrupados` : ""}</option>)}</select></Field>
              {form.substance_id ? <Field label="Formulação PK/PD"><select className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={form.formulation_id} onChange={(event) => setForm({ ...form, formulation_id: event.target.value })}><option value="">— sem formulação estruturada —</option>{formulations.map((formulation: any) => <option key={formulation.id} value={formulation.id}>{formulation.formulation_name} · {formulation.curve_model ?? formulation.route ?? ""}</option>)}</select></Field> : null}
            </TabsContent>

            <TabsContent value="regimen" className="mt-0 min-w-0 space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4"><Field label="Dose"><Input type="number" inputMode="decimal" step="any" value={form.current_dose} onChange={(event) => setForm({ ...form, current_dose: event.target.value })} /></Field><Field label="Unidade"><Input value={form.dose_unit} onChange={(event) => setForm({ ...form, dose_unit: event.target.value })} /></Field><Field label="Horário usual"><Input type="time" value={form.usual_time} onChange={(event) => setForm({ ...form, usual_time: event.target.value })} /></Field><Field label="Início"><Input type="date" value={form.start_date} onChange={(event) => setForm({ ...form, start_date: event.target.value })} /></Field></div>
              <Field label="Frequência"><Input value={form.frequency} onChange={(event) => setForm({ ...form, frequency: event.target.value })} placeholder="Ex.: 1x/dia pela manhã" /></Field>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Status"><select className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="ativo">Ativo</option><option value="planejado">Planejado / ainda não iniciado</option><option value="suspenso">Suspenso</option><option value="descontinuado">Descontinuado</option></select></Field>
                <Field label="Fim / suspensão"><Input type="date" value={form.end_date} onChange={(event) => setForm({ ...form, end_date: event.target.value })} /></Field>
              </div>
              {form.status === "suspenso" || form.status === "descontinuado" ? (
                <Field label="Motivo da suspensão/descontinuação"><Textarea rows={3} value={form.discontinuation_reason} onChange={(event) => setForm({ ...form, discontinuation_reason: event.target.value })} placeholder="Efeito adverso, ineficácia, decisão compartilhada…" /></Field>
              ) : null}
            </TabsContent>

            <TabsContent value="targets" className="mt-0 min-w-0 space-y-4">
              <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">Registrar por que este fármaco existe no esquema permite julgar depois se ele cumpriu o papel. Listas aceitam itens separados por vírgula.</div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Indicação"><Input value={form.indication} onChange={(event) => setForm({ ...form, indication: event.target.value })} placeholder="TDAH, depressão, insônia…" /></Field>
                <Field label="Alvo diagnóstico"><Input value={form.diagnostic_target} onChange={(event) => setForm({ ...form, diagnostic_target: event.target.value })} placeholder="Hipótese que este fármaco testa" /></Field>
              </div>
              <Field label="Sintomas-alvo"><Input value={form.target_symptoms} onChange={(event) => setForm({ ...form, target_symptoms: event.target.value })} placeholder="desatenção, procrastinação, irritabilidade" /></Field>
              <Field label="Melhoras esperadas"><Input value={form.expected_improvements} onChange={(event) => setForm({ ...form, expected_improvements: event.target.value })} placeholder="iniciativa, sono consolidado" /></Field>
              <Field label="Pioras possíveis"><Input value={form.potential_worsening} onChange={(event) => setForm({ ...form, potential_worsening: event.target.value })} placeholder="ansiedade, insônia, apatia afetiva" /></Field>
              <Field label="Critérios para reduzir ou parar"><Textarea rows={4} value={form.stop_or_reduce_criteria} onChange={(event) => setForm({ ...form, stop_or_reduce_criteria: event.target.value })} placeholder="O que precisa acontecer para desescalonar." /></Field>
            </TabsContent>

            <TabsContent value="response" className="mt-0 min-w-0 space-y-4">
              <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">Estas escalas representam a estimativa individual do paciente e não substituem o perfil canônico da substância.</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <ScoreField label="Benefício esperado" value={form.expected_benefit_0_100} onChange={(value) => setForm({ ...form, expected_benefit_0_100: value })} />
                <ScoreField label="Custo/risco esperado" value={form.expected_cost_0_100} onChange={(value) => setForm({ ...form, expected_cost_0_100: value })} />
                <ScoreField label="Sensibilidade individual" value={form.individual_sensitivity_0_100} onChange={(value) => setForm({ ...form, individual_sensitivity_0_100: value })} />
                <ScoreField label="Tolerância individual" value={form.individual_tolerance_0_100} onChange={(value) => setForm({ ...form, individual_tolerance_0_100: value })} />
                <ScoreField label="Risco individual de redose" value={form.redose_risk_individual_0_100} onChange={(value) => setForm({ ...form, redose_risk_individual_0_100: value })} />
                <ScoreField label="Risco de ativação/psicose" value={form.psychosis_activation_risk_individual_0_100} onChange={(value) => setForm({ ...form, psychosis_activation_risk_individual_0_100: value })} />
              </div>
              <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">Sensibilidades por eixo ajudam a explicar por que a mesma dose produz efeito diferente neste paciente.</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <ScoreField label="Sensibilidade à sedação" value={form.sedation_sensitivity_0_100} onChange={(value) => setForm({ ...form, sedation_sensitivity_0_100: value })} />
                <ScoreField label="Sensibilidade à insônia" value={form.insomnia_sensitivity_0_100} onChange={(value) => setForm({ ...form, insomnia_sensitivity_0_100: value })} />
                <ScoreField label="Sensibilidade autonômica" value={form.autonomic_sensitivity_0_100} onChange={(value) => setForm({ ...form, autonomic_sensitivity_0_100: value })} />
                <ScoreField label="Risco de uso problemático" value={form.abuse_risk_individual_0_100} onChange={(value) => setForm({ ...form, abuse_risk_individual_0_100: value })} />
                <ScoreField label="Risco de retirada/rebote" value={form.withdrawal_risk_individual_0_100} onChange={(value) => setForm({ ...form, withdrawal_risk_individual_0_100: value })} />
                <ScoreField label="Efeito residual no dia seguinte" value={form.individual_carry_over} onChange={(value) => setForm({ ...form, individual_carry_over: value })} />
              </div>
            </TabsContent>

            <TabsContent value="monitoring" className="mt-0 min-w-0 space-y-4">
              <Field label="Racional clínico individual"><Textarea rows={5} value={form.clinical_rationale} onChange={(event) => setForm({ ...form, clinical_rationale: event.target.value })} /></Field>
              <Field label="Plano de monitorização"><Textarea rows={5} value={form.monitoring_plan} onChange={(event) => setForm({ ...form, monitoring_plan: event.target.value })} /></Field>
              <Field label="Sinais de alerta"><Textarea rows={4} value={form.warning_signs} onChange={(event) => setForm({ ...form, warning_signs: event.target.value })} /></Field>
              <Field label="Notas do médico"><Textarea rows={4} value={form.physician_notes} onChange={(event) => setForm({ ...form, physician_notes: event.target.value })} /></Field>
              <Field label="Relato do paciente sobre esta medicação"><Textarea rows={4} value={form.patient_notes} onChange={(event) => setForm({ ...form, patient_notes: event.target.value })} /></Field>
            </TabsContent>
          </div>
        </Tabs>

        <div className="shrink-0 border-t bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:px-6">
          <div className="mb-2 grid grid-cols-2 gap-2 sm:hidden"><Button variant="outline" disabled={!previousSection} onClick={() => previousSection && setFormSection(previousSection)}><ChevronLeft className="mr-1 h-4 w-4" />Anterior</Button><Button variant="outline" disabled={!nextSection} onClick={() => nextSection && setFormSection(nextSection)}>Próxima<ChevronRight className="ml-1 h-4 w-4" /></Button></div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end"><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving}>{saving ? "Salvando…" : "Salvar e verificar"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader title="Tratamento do paciente" description="Marca real, canônico, formulação, dose e resposta individual. Todos os campos permanecem disponíveis no iPhone e no desktop." actions={<div className="flex flex-wrap gap-2"><Link to="/laboratorio-pkpd"><Button variant="outline"><BookOpen className="mr-1.5 h-4 w-4" />Laboratório PK/PD</Button></Link>{dialog}</div>} />
      <div className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,24rem)_1fr] sm:items-end"><Field label="Filtrar paciente"><select className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={patientFilter} onChange={(event) => setPatientFilter(event.target.value)}><option value="">Todos</option>{patients.map((patient: any) => <option key={patient.id} value={patient.id}>{patient.full_name}</option>)}</select></Field><div className="flex items-center gap-1.5 text-xs text-muted-foreground sm:pb-2"><Wand2 className="h-3.5 w-3.5 shrink-0" /><span className="break-words">Resolver marca → canônico não cria duplicatas.</span></div></div>
      {patientFilter && visibleMedications.length ? (
        <RegimenIntelligencePanel medications={visibleMedications as any} patientName={patients.find((patient: any) => patient.id === patientFilter)?.full_name} />
      ) : visibleMedications.length ? (
        <Card className="p-4 text-xs text-muted-foreground">Selecione um paciente acima para ver a leitura farmacológica do esquema (classes, cargas somadas, agenda do dia e lacunas).</Card>
      ) : null}
      {isLoading ? <Card className="p-6 text-sm text-muted-foreground">Carregando tratamentos…</Card> : error ? <Card className="p-6 text-sm text-destructive">Falha ao carregar: {(error as any)?.message}</Card> : visibleMedications.length === 0 ? <EmptyState icon={<Pill className="h-5 w-5" />} title="Nenhum tratamento cadastrado" description="Adicione a medicação real do paciente. O sistema vincula ao canônico quando possível." /> : <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{visibleMedications.map((medication: any) => <Card key={medication.id} className={`min-w-0 p-4 ${medication.status === "suspenso" ? "opacity-60" : ""}`}><div className="flex min-w-0 items-start justify-between gap-3"><div className="min-w-0 flex-1"><div className="break-words font-medium">{displayMedicationName(medication)}</div><div className="text-xs text-muted-foreground">{medication.patients?.full_name}</div><div className="mt-2 text-sm">{medication.current_dose ?? "—"} {medication.dose_unit ?? ""} · {medication.frequency ?? "frequência —"}</div><div className="mt-1 break-words text-[11px] text-muted-foreground">{medication.substances?.name ?? "sem canônico"} · {medication.substance_formulations?.formulation_name ?? "sem formulação"}</div>{medication.usual_time ? <div className="text-[11px] text-muted-foreground">Horário usual {String(medication.usual_time).slice(0, 5)}</div> : null}</div><div className="flex shrink-0 flex-col items-end gap-1"><Badge variant="outline">{medication.status}</Badge><Button size="icon" variant="ghost" onClick={() => setLongitudinalMedication(medication)} title="Acompanhamento longitudinal" aria-label={`Acompanhamento longitudinal de ${displayMedicationName(medication)}`}><HistoryIcon className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => openEdit(medication)} title="Editar" aria-label={`Editar ${displayMedicationName(medication)}`}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => suspend(medication)} title={medication.status === "suspenso" ? "Reativar" : "Suspender sem apagar"} aria-label={medication.status === "suspenso" ? "Reativar tratamento" : "Suspender tratamento sem apagar"}><ShieldOff className="h-4 w-4" /></Button></div></div></Card>)}</div>}
      {longitudinalMedication ? <MedicationLongitudinalPanel medication={longitudinalMedication} open={!!longitudinalMedication} onOpenChange={(value) => { if (!value) setLongitudinalMedication(null); }} /> : null}
    </div>
  );
}

function Field({ label, children }: any) {
  return <div className="min-w-0 space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label><div className="min-w-0">{children}</div></div>;
}

function ScoreField({ label, value, onChange }: { label: string; value: any; onChange: (value: string) => void }) {
  return <Field label={`${label} · 0–100`}><Input type="number" inputMode="numeric" min={0} max={100} value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
}
